import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import StudentEditModal from "./StudentEditModal";
import StudentRegisterModal from "./StudentRegisterModal";
import StudentViewModal from "./StudentViewModal";
import StudentBulkImportModal from "./StudentBulkImportModal";

function Students() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [createdStudent, setCreatedStudent] = useState(null);
  const [resetPasswordDetails, setResetPasswordDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return students;

    return students.filter((student) => {
      return (
        student.full_name?.toLowerCase().includes(term) ||
        student.student_number?.toLowerCase().includes(term) ||
        student.current_class?.toLowerCase().includes(term) ||
        student.email?.toLowerCase().includes(term)
      );
    });
  }, [students, searchTerm]);

  const totalActive = students.filter((student) => student.is_active).length;
  const totalInactive = students.length - totalActive;

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("students/");
      setStudents(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load students.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleStudentCreated = (student) => {
    setCreatedStudent(student);
    setShowRegisterModal(false);
    fetchStudents();
  };

  const handleStudentUpdated = (student) => {
    setStudents((prev) =>
      prev.map((item) => (item.id === student.id ? student : item))
    );
    setEditStudent(null);
    setViewStudent(null);
  };

  const resetStudentPassword = async (student) => {
    if (!window.confirm(`Reset password for ${student.full_name}?`)) return;

    try {
      const response = await axiosInstance.post(
        `students/${student.id}/reset-password/`
      );
      const data = response.data;

      setStudents((prev) =>
        prev.map((item) => (item.id === data.student.id ? data.student : item))
      );
      setResetPasswordDetails({
        full_name: data.student.full_name,
        student_number: data.student_number,
        temporary_password: data.temporary_password,
      });
      toast.success("Student password reset successfully.");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reset student password.");
    }
  };

  const copyStudentLogin = async () => {
    if (!createdStudent) return;

    const text = `Student: ${createdStudent.full_name}
Student Number: ${createdStudent.student_number}
Temporary Password: ${createdStudent.temporary_password}`;

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Student login details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  const copyResetPasswordDetails = async () => {
    if (!resetPasswordDetails) return;

    const text = `Student: ${resetPasswordDetails.full_name}
Student Number: ${resetPasswordDetails.student_number}
Temporary Password: ${resetPasswordDetails.temporary_password}`;

    try {
      await navigator.clipboard.writeText(text);
      toast.success("New password details copied.");
    } catch (error) {
      toast.info("Copy failed. Highlight and copy the details manually.");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>
            Students
          </h3>
          <p className="text-muted mb-0">
            Manage admissions, student records, health notes, and class placement.
          </p>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-light border"
            onClick={fetchStudents}
            title="Refresh students"
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button className="btn btn-outline-success" onClick={() => setShowBulkImport(true)}>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Import CSV
          </button>
          <button
            className="btn btn-success"
            onClick={() => setShowRegisterModal(true)}
          >
            <i className="bi bi-person-plus me-2"></i>
            Register Student
          </button>
        </div>
      </div>

      {createdStudent && (
        <div className="alert alert-success border-0 shadow-sm mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h6 className="fw-bold mb-1">Student registered successfully</h6>
              <div>
                <strong>{createdStudent.full_name}</strong> can login with student number{" "}
                <strong>{createdStudent.student_number}</strong>
              </div>
              <small className="text-muted">
                Temporary password: {createdStudent.temporary_password}
              </small>
            </div>
            <button className="btn btn-success btn-sm" onClick={copyStudentLogin}>
              <i className="bi bi-clipboard-check me-2"></i>
              Copy Details
            </button>
          </div>
        </div>
      )}

      {resetPasswordDetails && (
        <div className="alert alert-warning border-0 shadow-sm mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h6 className="fw-bold mb-1">Student password reset</h6>
              <div>
                <strong>{resetPasswordDetails.full_name}</strong> can login with student number{" "}
                <strong>{resetPasswordDetails.student_number}</strong>
              </div>
              <small className="text-muted">
                New temporary password: {resetPasswordDetails.temporary_password}
              </small>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-warning btn-sm"
                onClick={copyResetPasswordDetails}
              >
                <i className="bi bi-clipboard-check me-2"></i>
                Copy Details
              </button>
              <button
                className="btn btn-light btn-sm"
                onClick={() => setResetPasswordDetails(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted fw-bold">Total Students</small>
                <h3 className="fw-bold mb-0">{students.length}</h3>
              </div>
              <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
                <i className="bi bi-mortarboard fs-3"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted fw-bold">Active</small>
                <h3 className="fw-bold mb-0">{totalActive}</h3>
              </div>
              <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
                <i className="bi bi-check2-circle fs-3"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted fw-bold">Inactive</small>
                <h3 className="fw-bold mb-0">{totalInactive}</h3>
              </div>
              <div className="rounded-3 p-3 text-secondary" style={{ background: "#f3f4f6" }}>
                <i className="bi bi-pause-circle fs-3"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Student Records</h5>
            <input
              type="search"
              className="form-control"
              style={{ maxWidth: 320 }}
              placeholder="Search name, number, class..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="p-4 text-center text-muted">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading students...
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th>Student Number</th>
                    <th>Class</th>
                    <th>Emergency Contact</th>
                    <th>Health</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <div className="fw-bold">{student.full_name}</div>
                        <small className="text-muted">{student.email}</small>
                      </td>
                      <td>
                        <span className="badge text-bg-success">
                          {student.student_number}
                        </span>
                      </td>
                      <td>
                        <div>{student.current_class || "Not assigned"}</div>
                        <small className="text-muted">{student.stream || ""}</small>
                      </td>
                      <td>
                        <div>{student.emergency_contact_name || "Not set"}</div>
                        <small className="text-muted">
                          {student.emergency_contact_phone || ""}
                        </small>
                      </td>
                      <td>
                        {student.medical_conditions || student.allergies ? (
                          <span className="badge text-bg-warning">Notes</span>
                        ) : (
                          <span className="badge text-bg-light border">Clear</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            student.is_active ? "text-bg-success" : "text-bg-secondary"
                          }`}
                        >
                          {student.is_active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-light border me-2"
                          title="View profile"
                          onClick={() => setViewStudent(student)}
                        >
                          <i className="bi bi-eye text-success"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-light border"
                          title="Edit student"
                          onClick={() => setEditStudent(student)}
                        >
                          <i className="bi bi-pencil-square text-success"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-light border ms-2"
                          title="Reset password"
                          onClick={() => resetStudentPassword(student)}
                        >
                          <i className="bi bi-key text-warning"></i>
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredStudents.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No students found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <StudentBulkImportModal
        show={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={fetchStudents}
      />

      <StudentRegisterModal
        show={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onCreated={handleStudentCreated}
      />

      <StudentViewModal
        student={viewStudent}
        onClose={() => setViewStudent(null)}
        onEdit={(student) => {
          setViewStudent(null);
          setEditStudent(student);
        }}
      />

      <StudentEditModal
        student={editStudent}
        onClose={() => setEditStudent(null)}
        onUpdated={handleStudentUpdated}
      />
    </div>
  );
}

export default Students;
